/**
 * BlogAdmin.tsx — In-browser blog post composer (no backend).
 *
 * Generates a JSON snippet matching the shape used in
 * `src/content/blog-posts.json`. Authors can copy/download the snippet and
 * hand it back to the agent (or paste it into the JSON file) to publish.
 *
 * No persistence, no auth — purely a local authoring tool.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Copy, Download, Plus, Trash2, Check, Send } from "lucide-react";
import { Seo } from "@/components/seo/Seo";
import { ButtonCustom } from "@/components/ui/button-custom";
import { saveLocalPost, type BlogPostFull } from "@/lib/blogStorage";

const CATEGORIES = [
  "التجارة الإلكترونية",
  "التسويق الرقمي",
  "SEO",
  "الإعلانات المدفوعة",
  "تطوير المواقع",
];

interface Section {
  title: string;
  content: string;
}

const slugify = (s: string) =>
  s
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s-]/gu, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);

const todayISO = () => new Date().toISOString().slice(0, 10);

const Field = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: React.ReactNode;
  hint?: string;
}) => (
  <label className="block space-y-2">
    <span className="text-sm font-medium text-foreground/90">{label}</span>
    {children}
    {hint && <span className="block text-xs text-foreground/50">{hint}</span>}
  </label>
);

const inputCls =
  "w-full rounded-lg bg-surface-2 border border-foreground/10 px-4 py-2.5 text-sm text-foreground placeholder:text-foreground/30 focus:outline-none focus:border-sand/60 focus:ring-2 focus:ring-sand/20 transition";

const BlogAdmin = () => {
  const [id, setId] = useState("");
  const [idTouched, setIdTouched] = useState(false);
  const [title, setTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [date, setDate] = useState(todayISO());
  const [readTime, setReadTime] = useState("5 دقائق");
  const [image, setImage] = useState("");
  const [imageAlt, setImageAlt] = useState("");
  const [tagsRaw, setTagsRaw] = useState("");
  const [author, setAuthor] = useState("فريق إتقان التسويقي");
  const [authorId, setAuthorId] = useState("mohamed-alshehri");
  const [intro, setIntro] = useState("");
  const [sections, setSections] = useState<Section[]>([
    { title: "", content: "" },
  ]);
  const [copied, setCopied] = useState(false);
  const [publishMsg, setPublishMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const finalId = idTouched && id ? id : slugify(title) || "untitled-post";

  const tags = useMemo(
    () =>
      tagsRaw
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
    [tagsRaw],
  );

  const postObject = useMemo(
    () => ({
      [finalId]: {
        id: finalId,
        title,
        metaDescription,
        category,
        date,
        readTime,
        image,
        imageAlt,
        tags,
        author,
        authorId,
        intro,
        sections: sections.filter((s) => s.title || s.content),
      },
    }),
    [
      finalId,
      title,
      metaDescription,
      category,
      date,
      readTime,
      image,
      imageAlt,
      tags,
      author,
      authorId,
      intro,
      sections,
    ],
  );

  const json = JSON.stringify(postObject, null, 2);

  const updateSection = (i: number, patch: Partial<Section>) =>
    setSections((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));

  const addSection = () =>
    setSections((prev) => [...prev, { title: "", content: "" }]);

  const removeSection = (i: number) =>
    setSections((prev) => prev.filter((_, idx) => idx !== i));

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // ignore
    }
  };

  const download = () => {
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${finalId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const publish = () => {
    if (!title.trim()) {
      setPublishMsg({ ok: false, text: "أضف عنواناً للمقالة قبل النشر." });
      return;
    }
    const cleanSections = sections.filter((s) => s.title || s.content);
    const post: BlogPostFull = {
      id: finalId,
      title,
      metaDescription,
      category,
      date,
      readTime,
      image,
      imageAlt,
      tags,
      author,
      authorId,
      intro,
      sections: cleanSections,
      conclusion: "",
      faq: [],
      relatedPosts: [],
    };
    saveLocalPost(post);
    setPublishMsg({
      ok: true,
      text: "تم النشر! المقالة ظاهرة الآن في /blog على هذا المتصفح.",
    });
    setTimeout(() => setPublishMsg(null), 4000);
  };

  return (
    <>
      <Seo
        title="محرر المدونة | إتقان"
        description="أداة داخلية لإنشاء مقالات جديدة لمدونة إتقان."
        canonical="/blog/admin"
        noIndex
      />

      <div className="min-h-screen bg-surface pt-24 pb-16">
        <div className="container mx-auto px-4 max-w-6xl">
          {/* Header */}
          <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
            <div>
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-sand transition mb-3"
              >
                <ArrowLeft className="w-4 h-4" />
                العودة للمدونة
              </Link>
              <h1 className="text-3xl md:text-4xl font-display font-bold foil">
                محرر المقالات
              </h1>
              <p className="text-foreground/60 mt-2 text-sm md:text-base">
                املأ الحقول، ثم انسخ أو نزّل JSON وألصقه في{" "}
                <code className="text-sand">src/content/blog-posts.json</code>.
              </p>
            </div>
          </header>

          <div className="grid lg:grid-cols-5 gap-8">
            {/* Form */}
            <section className="lg:col-span-3 space-y-6">
              <div className="rounded-2xl border border-foreground/10 bg-surface-2/40 p-6 space-y-5">
                <h2 className="font-display text-lg text-sand">المعلومات الأساسية</h2>
                <Field label="عنوان المقالة">
                  <input
                    className={inputCls}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="مثال: أساسيات SEO للمتاجر السعودية"
                  />
                </Field>
                <Field
                  label="المعرّف (slug)"
                  hint="يُولَّد تلقائياً من العنوان. عدّله لو احتجت."
                >
                  <input
                    className={inputCls}
                    value={idTouched ? id : slugify(title)}
                    onChange={(e) => {
                      setIdTouched(true);
                      setId(slugify(e.target.value));
                    }}
                    placeholder="seo-basics-saudi-stores"
                  />
                </Field>
                <Field
                  label="الوصف الميتا"
                  hint="150–160 حرف يظهر في نتائج البحث."
                >
                  <textarea
                    className={`${inputCls} min-h-[80px] resize-y`}
                    value={metaDescription}
                    onChange={(e) => setMetaDescription(e.target.value)}
                    maxLength={300}
                  />
                </Field>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="التصنيف">
                    <select
                      className={inputCls}
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      {CATEGORIES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="مدة القراءة">
                    <input
                      className={inputCls}
                      value={readTime}
                      onChange={(e) => setReadTime(e.target.value)}
                    />
                  </Field>
                  <Field label="التاريخ">
                    <input
                      type="date"
                      className={inputCls}
                      value={date}
                      onChange={(e) => setDate(e.target.value)}
                    />
                  </Field>
                  <Field label="الوسوم" hint="افصل بينها بفاصلة (،)">
                    <input
                      className={inputCls}
                      value={tagsRaw}
                      onChange={(e) => setTagsRaw(e.target.value)}
                      placeholder="SEO, متاجر إلكترونية, السعودية"
                    />
                  </Field>
                </div>
              </div>

              <div className="rounded-2xl border border-foreground/10 bg-surface-2/40 p-6 space-y-5">
                <h2 className="font-display text-lg text-sand">صورة الغلاف</h2>
                <Field
                  label="رابط الصورة"
                  hint="يفضّل رابط من Unsplash أو رابط مرفوع على CDN."
                >
                  <input
                    className={inputCls}
                    value={image}
                    onChange={(e) => setImage(e.target.value)}
                    placeholder="https://images.unsplash.com/..."
                  />
                </Field>
                <Field label="وصف الصورة (Alt)">
                  <input
                    className={inputCls}
                    value={imageAlt}
                    onChange={(e) => setImageAlt(e.target.value)}
                  />
                </Field>
                {image && (
                  <div className="rounded-lg overflow-hidden border border-foreground/10 aspect-[16/9] bg-surface">
                    <img
                      src={image}
                      alt={imageAlt}
                      className="w-full h-full object-cover"
                      onError={(e) => (e.currentTarget.style.opacity = "0.2")}
                    />
                  </div>
                )}
              </div>

              <div className="rounded-2xl border border-foreground/10 bg-surface-2/40 p-6 space-y-5">
                <h2 className="font-display text-lg text-sand">الكاتب</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="اسم الكاتب">
                    <input
                      className={inputCls}
                      value={author}
                      onChange={(e) => setAuthor(e.target.value)}
                    />
                  </Field>
                  <Field label="معرّف الكاتب">
                    <input
                      className={inputCls}
                      value={authorId}
                      onChange={(e) => setAuthorId(e.target.value)}
                    />
                  </Field>
                </div>
              </div>

              <div className="rounded-2xl border border-foreground/10 bg-surface-2/40 p-6 space-y-5">
                <h2 className="font-display text-lg text-sand">المحتوى</h2>
                <Field label="مقدمة المقالة">
                  <textarea
                    className={`${inputCls} min-h-[120px] resize-y leading-7`}
                    value={intro}
                    onChange={(e) => setIntro(e.target.value)}
                  />
                </Field>

                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-foreground/90">
                      الأقسام
                    </span>
                    <button
                      type="button"
                      onClick={addSection}
                      className="inline-flex items-center gap-1.5 text-xs text-sand hover:text-sand/80 transition"
                    >
                      <Plus className="w-4 h-4" />
                      إضافة قسم
                    </button>
                  </div>

                  {sections.map((s, i) => (
                    <div
                      key={i}
                      className="rounded-xl border border-foreground/10 bg-surface/60 p-4 space-y-3 relative"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-foreground/50">
                          قسم {i + 1}
                        </span>
                        {sections.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeSection(i)}
                            className="text-foreground/40 hover:text-red-400 transition"
                            aria-label="حذف القسم"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <input
                        className={inputCls}
                        placeholder="عنوان القسم"
                        value={s.title}
                        onChange={(e) => updateSection(i, { title: e.target.value })}
                      />
                      <textarea
                        className={`${inputCls} min-h-[140px] resize-y leading-7`}
                        placeholder="نص القسم..."
                        value={s.content}
                        onChange={(e) =>
                          updateSection(i, { content: e.target.value })
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
            </section>

            {/* Output */}
            <aside className="lg:col-span-2">
              <div className="sticky top-24 space-y-4">
                <div className="rounded-2xl border border-sand/40 bg-gradient-to-br from-sand/10 to-transparent p-5 space-y-3">
                  <div>
                    <h2 className="font-display text-sand text-lg">نشر المقالة</h2>
                    <p className="text-xs text-foreground/60 mt-1 leading-5">
                      ينشر فوراً في صفحة <code className="text-sand">/blog</code> على هذا المتصفح فقط (localStorage).
                    </p>
                  </div>
                  <ButtonCustom
                    variant="foil"
                    onClick={publish}
                    className="w-full"
                    disabled={!title.trim()}
                  >
                    <Send className="w-4 h-4" />
                    نشر إلى المدونة
                  </ButtonCustom>
                  {publishMsg && (
                    <p
                      role="status"
                      className={`text-xs text-center ${publishMsg.ok ? "text-sand" : "text-red-400"}`}
                    >
                      {publishMsg.text}
                    </p>
                  )}
                  <Link
                    to={`/blog/${finalId}`}
                    target="_blank"
                    rel="noreferrer"
                    className="block text-center text-xs text-foreground/60 hover:text-sand transition"
                  >
                    معاينة في صفحة جديدة ↗
                  </Link>
                </div>

                <div className="rounded-2xl border border-sand/20 bg-surface-2/60 p-5">
                  <div className="flex items-center justify-between mb-3">
                    <h2 className="font-display text-sand">JSON الناتج</h2>
                    <div className="flex gap-2">
                      <ButtonCustom
                        size="sm"
                        variant="ghost"
                        onClick={copy}
                        aria-label="نسخ"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" /> تم
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" /> نسخ
                          </>
                        )}
                      </ButtonCustom>
                      <ButtonCustom
                        size="sm"
                        variant="ghost"
                        onClick={download}
                        aria-label="تنزيل"
                      >
                        <Download className="w-4 h-4" /> تنزيل
                      </ButtonCustom>
                    </div>
                  </div>
                  <pre
                    dir="ltr"
                    className="text-[11px] leading-relaxed text-foreground/80 bg-surface/80 rounded-lg p-3 max-h-[560px] overflow-auto border border-foreground/10"
                  >
                    {json}
                  </pre>
                </div>

                <div className="rounded-2xl border border-foreground/10 bg-surface-2/30 p-5 text-xs text-foreground/60 leading-6">
                  <p className="text-foreground/80 font-medium mb-2">كيف تنشر؟</p>
                  <ol className="space-y-1 list-decimal pr-4">
                    <li>انسخ كائن المقالة (بدون الأقواس الخارجية).</li>
                    <li>
                      افتح{" "}
                      <code className="text-sand">
                        src/content/blog-posts.json
                      </code>
                    </li>
                    <li>أضف الكائن داخل الكائن الرئيسي وامنحه فاصلة.</li>
                    <li>احفظ، وسيظهر المقال في صفحة المدونة فوراً.</li>
                  </ol>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogAdmin;
